// Theme variant constants
export const THEME_VARIANTS = {
  LIGHT: 'light',
  DARK: 'dark',
  AUTO: 'auto'
};

// Base theme configuration
export const theme = {
  // Colors - Light Mode
  light: {
    primaryColor: '#1E40AF',
    secondaryColor: '#3B82F6',
    textColor: '#333333',
    backgroundColor: '#FFFFFF',
    secondaryBackground: '#F5F5F5',
    headerBackground: '#0F172A',
    
    // Menu Styles
    menuBackground: 'rgba(15, 23, 42, 0.85)',
    menuBackdropFilter: 'blur(15px)',
    menuBorderColor: 'rgba(30, 64, 175, 0.15)',
    menuShadow: '0 12px 32px rgba(0, 0, 0, 0.2)',
    menuHoverBackground: 'rgba(30, 64, 175, 0.08)',
    menuTextColor: '#FFFFFF',
    menuTextHoverColor: '#3B82F6',
    
    // Liquid Glass specific
    glassBackground: 'rgba(248, 250, 252, 0.6)',
    glassBorder: 'rgba(148, 163, 184, 0.25)',
    glassText: '#333333',
    glassHeading: '#1E40AF',
    glassLink: '#1E40AF',
    glassLinkHover: '#1E3A8A'
  },
  
  // Colors - Dark Mode
  dark: {
    primaryColor: '#3B82F6',
    secondaryColor: '#60A5FA',
    textColor: 'rgba(248, 250, 252, 0.95)',
    // DWEB-170: was '#0F172A' (the aurora-era slate), then graphite #1C1C1E,
    // then pure black on the owner's call mid-release. See
    // DynamicSkyBackground.js — that is the element which actually paints it,
    // and it carries the measurement behind the choice.
    backgroundColor: '#000000',
    secondaryBackground: '#1E293B',
    headerBackground: '#0F172A',
    
    // Menu Styles
    menuBackground: 'rgba(30, 41, 59, 0.9)',
    menuBackdropFilter: 'blur(15px)',
    menuBorderColor: 'rgba(59, 130, 246, 0.2)',
    menuShadow: '0 12px 32px rgba(0, 0, 0, 0.4)',
    menuHoverBackground: 'rgba(59, 130, 246, 0.15)',
    menuTextColor: 'rgba(248, 250, 252, 0.95)',
    menuTextHoverColor: '#60A5FA',
    
    // Liquid Glass specific
    glassBackground: 'rgba(30, 41, 59, 0.4)',
    glassBorder: 'rgba(148, 163, 184, 0.2)',
    glassText: 'rgba(248, 250, 252, 0.95)',
    glassHeading: '#93C5FD',
    glassLink: '#93C5FD',
    glassLinkHover: '#FFFFFF'
  },
  
  // Common Styles (theme-agnostic)
  common: {
    sectionPadding: '80px 0',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
    
    // Typography
    fontFamily: "'Roboto', sans-serif",
    headingMargin: '60px',
    paragraphLineHeight: '1.6',
    
    fonts: {
      heading: "'Roboto', sans-serif",
      body: "'Roboto', sans-serif"
    }
  },
  
  // Liquid Glass Theme Integration with Variants
  liquidGlass: {
    variants: {
      light: {
        background: 'rgba(248, 250, 252, 0.6)',
        border: 'rgba(148, 163, 184, 0.25)',
        shadow: '0 8px 32px -8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
        text: '#333333',
        heading: '#1E40AF'
      },
      dark: {
        background: 'rgba(30, 41, 59, 0.4)',
        border: 'rgba(148, 163, 184, 0.2)',
        shadow: '0 8px 32px -8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
        text: 'rgba(248, 250, 252, 0.95)',
        heading: '#93C5FD'
      },
      primary: 'rgba(30, 64, 175, 0.25)',
      secondary: 'rgba(59, 130, 246, 0.25)',
      accent: 'rgba(160, 160, 160, 0.25)'
    }
  }
};

// Helper function to get theme values based on variant
export const getThemeForVariant = (variant = THEME_VARIANTS.AUTO, isDarkMode = false) => {
  let effectiveMode;
  
  if (variant === THEME_VARIANTS.DARK) {
    effectiveMode = 'dark';
  } else if (variant === THEME_VARIANTS.LIGHT) {
    effectiveMode = 'light';
  } else {
    effectiveMode = isDarkMode ? 'dark' : 'light';
  }
  
  return {
    ...theme.common,
    ...theme[effectiveMode],
    isDarkMode: effectiveMode === 'dark',
    variant,
    liquidGlass: theme.liquidGlass.variants[effectiveMode]
  };
};

// Backward compatibility - default light theme
export const defaultTheme = {
  ...theme.common,
  ...theme.light,
  isDarkMode: false,
  colors: {
    primary: theme.light.primaryColor,
    accent: theme.light.secondaryColor,
    text: theme.light.textColor,
    background: theme.light.backgroundColor,
    backgroundLight: theme.light.secondaryBackground
  },
  liquidGlass: theme.liquidGlass.variants.light
};
